<template>
  <div class="conversation-analysis-modal" dir="rtl">

    <!-- Step 1: Upload -->
    <div v-if="step === 'upload'" class="step-upload">
      <div class="upload-section">
        <label class="file-label">
          <input
            type="file"
            accept="audio/*"
            @change="onFileSelected"
            ref="fileInput"
            class="file-input"
          />
          <span class="file-cta">
            <span class="file-text">{{ selectedFileName || 'בחר קובץ שמע' }}</span>
          </span>
        </label>

        <button
          class="btn btn-primary"
          :disabled="!selectedFile"
          @click="submitAudio"
        >
          שלח לניתוח
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>

    <!-- Step 2: Processing -->
    <div v-if="step === 'processing'" class="step-processing">
      <div class="processing-indicator">
        <div class="spinner"></div>
        <p class="processing-text">מנתח את השיחה...</p>
        <p class="processing-sub">תמלול, זיהוי דוברים, חילוץ משפטים, סיווג, פענוח</p>
      </div>
    </div>

    <!-- Step 3: Review -->
    <div v-if="step === 'review'" class="step-review">
      <div class="review-summary">
        <span>זוהו <strong>{{ reviewItems.length }}</strong> פריטים</span>
        <span v-if="resultStatementsCount"> | {{ resultStatementsCount }} משפטים</span>
      </div>

      <div class="table-wrapper">
        <table class="review-table">
          <thead>
            <tr>
              <th class="col-check">
                <input type="checkbox" :checked="allChecked" @change="toggleAll" />
              </th>
              <th class="col-type">סוג</th>
              <th class="col-name">שם</th>
              <th class="col-field">שדה</th>
              <th class="col-value">ערך</th>
              <th class="col-source">משפט מקור</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reviewItems" :key="item.id" :class="{ 'row-unchecked': !item.checked }">
              <td class="col-check">
                <input type="checkbox" v-model="item.checked" />
              </td>
              <td class="col-type">{{ formatResourceType(item.resource_type) }}</td>
              <td class="col-name">{{ item.instance_name }}</td>
              <td class="col-field">{{ item.attribute_name }}</td>
              <td class="col-value">
                <input type="text" v-model="item.value" class="value-input" />
              </td>
              <td class="col-source">
                <span class="source-text">{{ item._source_statement }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="review-actions">
        <button
          class="btn btn-primary"
          :disabled="isApproving || checkedCount === 0"
          @click="approveItems"
        >
          {{ isApproving ? 'מעבד...' : 'אשר נבחרים (' + checkedCount + ')' }}
        </button>
        <button
          class="btn btn-secondary"
          :disabled="isApproving"
          @click="onComplete()"
        >
          דלג
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConversationAnalysisModal',
  props: {
    applicantId: { type: Number, required: true },
    offerId: { type: Number, default: null },
    onComplete: { type: Function, required: true }
  },
  data: function () {
    return {
      step: 'upload',
      selectedFile: null,
      selectedFileName: '',
      reviewItems: [],
      resultStatementsCount: null,
      isApproving: false,
      errorMessage: null
    }
  },
  computed: {
    allChecked: function () {
      return this.reviewItems.length > 0 && this.reviewItems.every(function (i) { return i.checked })
    },
    checkedCount: function () {
      return this.reviewItems.filter(function (i) { return i.checked }).length
    }
  },
  methods: {
    onFileSelected: function (event) {
      var file = event.target.files[0]
      this.selectedFile = file || null
      this.selectedFileName = file ? file.name : ''
      this.errorMessage = null
    },

    submitAudio: function () {
      if (!this.selectedFile) return

      var self = this
      self.errorMessage = null
      self.step = 'processing'

      var formData = new FormData()
      formData.append('audio', self.selectedFile)
      formData.append('applicant_id', self.applicantId)
      formData.append('operation', 'rebase')
      if (self.offerId) {
        formData.append('offer_id', self.offerId)
      }

      self.$network_utils.submitConversationAnalysis(formData)
        .then(function (response) {
          var items = response.items || []
          self.resultStatementsCount = response.statements_count

          if (items.length === 0) {
            self.$utils.emit('show_toast', { text: 'לא זוהו פריטים בשיחה', type: 'info' })
            self.onComplete()
            return
          }

          self.reviewItems = items.map(function (item) {
            return Object.assign({}, item, { checked: true })
          })
          self.step = 'review'
        })
        .catch(function (error) {
          console.error('[ConversationAnalysis] Submit error:', error)
          var msg = (error && error.response && error.response.data && error.response.data.error) || 'שגיאה בניתוח השיחה'
          self.errorMessage = msg
          self.step = 'upload'
        })
    },

    approveItems: function () {
      var self = this
      self.isApproving = true
      self.errorMessage = null

      var approved = self.reviewItems
        .filter(function (i) { return i.checked })
        .map(function (i) { return { id: i.id, value: i.value } })

      var rejected = self.reviewItems
        .filter(function (i) { return !i.checked })
        .map(function (i) { return i.id })

      self.$network_utils.approveConversationAnalysis({
        approved_items: approved,
        rejected_ids: rejected
      })
        .then(function (response) {
          self.isApproving = false
          self.$utils.emit('show_toast', {
            text: 'עובדו ' + response.processed + ' פריטים',
            type: 'success'
          })
          self.onComplete()
        })
        .catch(function (error) {
          self.isApproving = false
          console.error('[ConversationAnalysis] Approve error:', error)
          self.errorMessage = (error && error.response && error.response.data && error.response.data.error) || 'שגיאה באישור הפריטים'
        })
    },

    toggleAll: function () {
      var newVal = !this.allChecked
      this.reviewItems.forEach(function (i) { i.checked = newVal })
    },

    formatResourceType: function (type) {
      var map = {
        Skill: 'מיומנות',
        JobTitle: 'תפקיד',
        Applicant: 'מועמד',
        Offer: 'הצעה'
      }
      return map[type] || type
    }
  }
}
</script>

<style scoped>
.conversation-analysis-modal {
  padding: 1.5rem;
  min-height: 200px;
}

/* Upload Step */
.step-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 0;
}

.upload-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.file-input {
  display: none;
}

.file-label {
  cursor: pointer;
}

.file-cta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border: 2px dashed #adb5bd;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.2s;
  font-size: 1.1rem;
}

.file-cta:hover {
  border-color: #667eea;
  background: #f0f2ff;
}

/* Processing Step */
.step-processing {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
}

.processing-indicator {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-text {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.processing-sub {
  font-size: 0.85rem;
  color: #868e96;
  margin: 0;
}

/* Buttons */
.btn {
  padding: 0.6rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:not(:disabled):hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #e9ecef;
  color: #495057;
}

.btn-secondary:not(:disabled):hover {
  background: #dee2e6;
}

/* Review Step */
.step-review {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.review-summary {
  font-size: 0.95rem;
  color: #495057;
  padding: 0.5rem 0;
}

.table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.review-table thead {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  z-index: 1;
}

.review-table th,
.review-table td {
  padding: 0.6rem 0.75rem;
  text-align: right;
  border-bottom: 1px solid #dee2e6;
}

.review-table th {
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.review-table tbody tr:hover {
  background: #f8f9fa;
}

.row-unchecked {
  opacity: 0.5;
}

.col-check {
  width: 40px;
  text-align: center !important;
}

.col-type { width: 80px; }
.col-name { width: 120px; }
.col-field { width: 120px; }
.col-value { min-width: 200px; }
.col-source { min-width: 180px; }

.value-input {
  width: 100%;
  padding: 0.25rem 0.4rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.85rem;
  direction: rtl;
  color: #2c3e50;
}

.value-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.source-text {
  font-size: 0.8rem;
  color: #868e96;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-start;
  padding-top: 0.5rem;
}

.error-message {
  background: #fff5f5;
  color: #c92a2a;
  padding: 0.75rem;
  border-radius: 6px;
  text-align: center;
  margin-top: 1rem;
}
</style>
